/**
 * Chunk 180: Protocol Risk API Client
 * GET /analytics/protocol-risk/:protocolSlug
 * GET /analytics/protocol-risk/user/:userId
 */

import apiClient from './client';

export interface ProtocolRiskProfile {
  id: string;
  protocol_slug: string;
  protocol_name: string;
  chain: string;
  risk_score: number | null;
  risk_grade: 'A' | 'B' | 'C' | 'D' | 'F' | null;
  has_audit: boolean;
  audit_firm: string | null;
  audit_date: string | null;
  exploit_count: number;
  total_exploit_usd: string;
  tvl_usd: string | null;
  days_deployed: number | null;
  last_updated: string;
  // enriched from join query
  position_value_usd?: string;
}

export interface UserProtocolRiskResponse {
  userId: string;
  highRiskCount: number;
  profiles: ProtocolRiskProfile[];
}

export interface ProtocolRiskDetailResponse {
  profile: ProtocolRiskProfile;
}

export async function getProtocolRiskBySlug(
  protocolSlug: string
): Promise<ProtocolRiskDetailResponse> {
  const res = await apiClient.get<ProtocolRiskDetailResponse>(
    `/analytics/protocol-risk/${encodeURIComponent(protocolSlug)}`
  );
  return res.data;
}

export async function getUserProtocolRisk(
  userId: string
): Promise<UserProtocolRiskResponse> {
  const res = await apiClient.get<UserProtocolRiskResponse>(
    `/analytics/protocol-risk/user/${encodeURIComponent(userId)}`
  );
  return res.data;
}

/** Returns colour classes for a risk grade badge */
export function getRiskGradeClasses(grade: string | null): string {
  switch (grade) {
    case 'A':
      return 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30';
    case 'B':
      return 'bg-emerald-500/10 text-emerald-300 border border-emerald-500/20';
    case 'C':
      return 'bg-amber-500/20 text-amber-400 border border-amber-500/30';
    case 'D':
      return 'bg-orange-500/20 text-orange-400 border border-orange-500/30';
    case 'F':
      return 'bg-red-500/20 text-red-400 border border-red-500/30';
    default:
      return 'bg-surface-hover text-text-muted border border-border';
  }
}

/** Returns progress bar colour for a risk score (0=green, 100=red) */
export function getRiskScoreBarColor(score: number | null): string {
  if (score === null) return 'bg-surface-hover';
  if (score <= 20) return 'bg-emerald-500';
  if (score <= 40) return 'bg-emerald-400';
  if (score <= 60) return 'bg-amber-400';
  if (score <= 80) return 'bg-orange-400';
  return 'bg-red-500';
}

/** Format TVL to compact string */
export function formatTvl(tvl: string | null): string {
  if (!tvl) return 'Unknown';
  const n = parseFloat(tvl);
  if (isNaN(n)) return 'Unknown';
  if (n >= 1_000_000_000) return `$${(n / 1_000_000_000).toFixed(2)}B`;
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000) return `$${(n / 1_000).toFixed(2)}K`;
  return `$${n.toFixed(2)}`;
}
